import {Component, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from "@angular/forms";
import {ActivatedRoute, Router} from "@angular/router";
import {TokenDataProviderService} from "../../../core/services/token-data-provider.service";
import {LoaderService} from "../../../core/services/loader.service";
import {ResponseErrorsHandlerService} from "../../../core/services/response-errors-handler.service";
import {ResponseNotificationService} from "../../../core/services/response-notification.service";
import {UserDataProviderService} from "../../user/settings/user.data-provider.service";
import {RegistrationRestService} from "../../../api/services/registration.rest.service";
import {AuthApiModel} from "../../../api/models/auth.api.model";
import {Oauth2UserApiModel} from "../../../api/models/oauth2-user.api.model";
import {TranslateService} from "@ngx-translate/core";
import {JwtService} from "../../../core/services/jwt.service";
import {PasswordValidator} from "../../../core/validators/password-validator";
import {StaticPageRestService} from '../../../api/services/static-page.rest.service';
import {StaticPageApiModel} from '../../../api/models/static-page.api.model';
import {Subscription} from 'rxjs';
import {AppConfigRestService} from "../../../api/services/app-config.rest.service";
import {AppConfigApiModel} from "../../../api/models/app-config.api-model";
import {appChannelCode} from '../../../core/feature-flags/app-channel';
import {AuthReturnUrlService} from '../../../core/services/auth-return-url.service';

@Component({
  selector: 'app-complete_registration',
  templateUrl: './complete-registration.component.html',
  styleUrls: ['./complete-registration.component.scss'],
  providers: [UserDataProviderService]
})
export class CompleteRegistrationComponent implements OnInit {

  public userAM: Oauth2UserApiModel = this.route.snapshot.data['currentUser'];
  public form: UntypedFormGroup;
  public error: string;
  public termsRoute = '/pages/terms';
  public privacyPolicyRoute = '/pages/privacy-policy';
  public showAccessCode = false;
  public appConfig: AppConfigApiModel | null = null;
  private subscriptions: Subscription[] = [];

  constructor(
    private fb: UntypedFormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private ts: TokenDataProviderService,
    private ls: LoaderService,
    private reh: ResponseErrorsHandlerService,
    private rs: RegistrationRestService,
    private rns: ResponseNotificationService,
    private translate: TranslateService,
    private readonly jwt: JwtService,
    private readonly staticPageRS: StaticPageRestService,
    private readonly appConfigRS: AppConfigRestService,
    private readonly returnUrls: AuthReturnUrlService,
  ) {
  }

  ngOnInit() {
    this.returnUrls.remember(this.route.snapshot.queryParamMap.get('returnUrl'));
    this.createForm();
    this.loadLegalRoutes();
    this.loadRegistrationSettings();
    this.subscriptions.push(this.translate.onLangChange.subscribe(() => {
      this.loadLegalRoutes();
      this.loadRegistrationSettings();
    }));
  }

  createForm() {
    this.form = this.fb.group(
      {
        username: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(20)]],
        password: this.fb.group(
          {
            first: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(50)]],
            second: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(50)]],
          },
          {validator: PasswordValidator.matchPassword}
        ),
        terms: ['', [Validators.requiredTrue]],
        accessCode: [null, [Validators.pattern(/^[A-Za-z0-9]{6}$/)]],
        channelCode: [appChannelCode()],
        localeCode: [this.translate.currentLang || this.translate.getDefaultLang() || 'pl-PL']
      }
    );
  }

  login() {
    this.ls.show();
    const accessCode = this.form.get('accessCode')?.value;
    if (accessCode === '') {
      this.form.get('accessCode')?.setValue(null);
    }

    const payload: Oauth2UserApiModel = {
      ...this.form.getRawValue(),
      email: this.userAM?.email || this.ts.getJwtUser().email
    };

    this.rs.completeRegistration(payload).subscribe(
      (successResponse: AuthApiModel) => {
        this.jwt.setSession(successResponse, true)
        this.ts.refreshUser().subscribe();
        this.router.navigateByUrl(this.returnUrls.consume());
      },
      errorResponse => {
        this.reh.handleFormErrors(this.form, errorResponse);
        this.rns.emitError(this.translate.instant('shared.complete_oauth2_registration.message.error'));
      }
    ).add(() => {
      this.ls.hide();
    });
  }

  private loadLegalRoutes(): void {
    this.staticPageRS.getNavigation('landing_footer').subscribe({
      next: (pages: StaticPageApiModel[]) => {
        const termsPage = pages.find((page) => page.documentType === 'TERMS');
        const privacyPage = pages.find((page) => page.documentType === 'PRIVACY_POLICY');

        this.termsRoute = termsPage ? this.staticPageRS.buildPublicRoute(termsPage) : '/pages/terms';
        this.privacyPolicyRoute = privacyPage ? this.staticPageRS.buildPublicRoute(privacyPage) : '/pages/privacy-policy';
      }
    });
  }

  private loadRegistrationSettings(): void {
    const localeCode = this.translate.currentLang || this.translate.getDefaultLang() || 'pl-PL';
    this.form.get('localeCode')?.setValue(localeCode);

    this.appConfigRS.getCurrent(appChannelCode(), localeCode).subscribe({
      next: (config: AppConfigApiModel) => {
        this.appConfig = config;
        this.showAccessCode = !!config.oauthCompletionAccessCodeEnabled;

        const control = this.form.get('accessCode');
        const validators = [Validators.pattern(/^[A-Za-z0-9]{6}$/)];

        if (config.oauthCompletionAccessCodeEnabled) {
          validators.unshift(Validators.required);
        }

        control?.setValidators(validators);
        control?.updateValueAndValidity();
      }
    });
  }
}
